import React, { useEffect, useState, useCallback } from "react";
import { HeartPulse, UploadCloud, Loader2, FileText, Video, Download, ClipboardCheck } from "lucide-react";
import { useAuth } from "../AuthContext";
import { API_URL } from "../config";

const MODALITIES = [
  { id: "ecg", label: "ЭКГ" },
  { id: "holter", label: "Холтер (суточное ЭКГ)" },
  { id: "smad", label: "СМАД" },
];

const KNOWN_MANUFACTURERS = [
  "Schiller", "Astrocard (Медитек)", "Инкарт", "BTL", "Cardioline",
  "Nihon Kohden", "GE Healthcare", "Philips", "Mortara", "Валента", "ДМС",
];

const STATUS_LABEL = {
  new: { label: "Новая", color: "#6b7280" },
  in_review: { label: "Назначен эксперт", color: "#d97706" },
  scheduled: { label: "Назначен онлайн-разбор", color: "#007c84" },
  answered: { label: "Есть заключение", color: "#15803d" },
};

function modalityLabel(id) {
  return MODALITIES.find(m => m.id === id)?.label || id;
}

function fmtSize(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("ru-RU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

function RequestCard({ req, token, canConclude, onConcluded }) {
  const [showConclude, setShowConclude] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const status = STATUS_LABEL[req.status] || { label: req.status, color: "#6b7280" };

  const submitConclusion = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/second-opinion/${req.id}/conclusion`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ conclusion: text.trim() }),
      });
      if (r.ok) { onConcluded && onConcluded(await r.json()); setShowConclude(false); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, color: "#1f2937", display: "flex", alignItems: "center", gap: 6 }}>
            <HeartPulse size={16} strokeWidth={2.2} color="#e5005b" />
            {modalityLabel(req.modality)}{req.manufacturer && ` · ${req.manufacturer}`}
          </div>
          <div style={{ color: "#888", fontSize: "0.82rem", marginTop: 2 }}>
            От {req.requester_name} · {fmtDateTime(req.created_at)}
          </div>
        </div>
        <span style={{ background: status.color, color: "#fff", borderRadius: 999, padding: "3px 12px", fontSize: "0.78rem", fontWeight: 700 }}>
          {status.label}
        </span>
      </div>

      {req.description && <p style={{ color: "#374151", fontSize: "0.92rem", marginTop: 10 }}>{req.description}</p>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        {req.files.map(f => (
          <a key={f.id} href={`${API_URL}/second-opinion/${req.id}/files/${f.id}?token=${token}`}
            target="_blank" rel="noopener noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 5, background: "#f3f4f6", borderRadius: 8, padding: "5px 10px", fontSize: "0.82rem", color: "#374151", textDecoration: "none" }}>
            <FileText size={13} /> {f.original_name} <span style={{ color: "#999" }}>({fmtSize(f.size)})</span>
            <Download size={12} />
          </a>
        ))}
      </div>

      {req.reviewer_name && (
        <p style={{ color: "#6b7280", fontSize: "0.85rem", marginTop: 10 }}>Эксперт: {req.reviewer_name}</p>
      )}

      {req.jitsi_room && (
        <div style={{ marginTop: 10, background: "#e6f6f7", border: "1.5px solid #bfdbfe", borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#006870", fontWeight: 700, fontSize: "0.88rem" }}>
            <Video size={15} /> Онлайн-разбор {req.event_date && `назначен на ${fmtDateTime(req.event_date)}`}
          </div>
          <a href={`https://meet.jit.si/${req.jitsi_room}`} target="_blank" rel="noopener noreferrer"
            style={{ color: "#006870", fontSize: "0.85rem", fontWeight: 600 }}>
            Перейти в комнату разбора →
          </a>
        </div>
      )}

      {req.conclusion && (
        <div style={{ marginTop: 10, background: "#fff1f6", border: "1.5px solid #ffc2d9", borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#e5005b", fontWeight: 700, fontSize: "0.85rem", marginBottom: 4 }}>
            <ClipboardCheck size={14} /> Заключение эксперта
          </div>
          <div style={{ color: "#007c84", fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>{req.conclusion}</div>
        </div>
      )}

      {canConclude && !req.conclusion && (
        <div style={{ marginTop: 10 }}>
          {!showConclude ? (
            <button onClick={() => setShowConclude(true)} style={{ background: "#e5005b", padding: "6px 14px", fontSize: "0.85rem" }}>
              Дать заключение
            </button>
          ) : (
            <div>
              <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
                placeholder="Опишите заключение по записи…"
                style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #c4ceda", fontSize: "0.9rem", boxSizing: "border-box" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button onClick={submitConclusion} disabled={busy} style={{ background: "#15803d", padding: "6px 14px", fontSize: "0.85rem" }}>
                  {busy ? "Отправка…" : "Отправить заключение"}
                </button>
                <button onClick={() => setShowConclude(false)} style={{ background: "#9ca3af", padding: "6px 14px", fontSize: "0.85rem" }}>
                  Отмена
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubmitForm({ token, onCreated }) {
  const [modality, setModality] = useState("ecg");
  const [manufacturer, setManufacturer] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async e => {
    e.preventDefault();
    if (selectedFiles.length === 0) { setError("Прикрепите хотя бы один файл записи"); return; }
    setBusy(true);
    setError("");
    const form = new FormData();
    form.append("modality", modality);
    form.append("manufacturer", manufacturer);
    form.append("description", description);
    selectedFiles.forEach(f => form.append("files", f));
    try {
      const r = await fetch(`${API_URL}/second-opinion`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось отправить заявку");
      setManufacturer(""); setDescription(""); setSelectedFiles([]);
      onCreated(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card" style={{ maxWidth: 640 }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
        <label style={{ fontSize: "0.85rem" }}>
          Методика
          <select value={modality} onChange={e => setModality(e.target.value)}
            style={{ display: "block", padding: "8px 10px", borderRadius: 8, border: "1px solid #c4ceda", marginTop: 4 }}>
            {MODALITIES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
        <label style={{ fontSize: "0.85rem", flex: 1, minWidth: 200 }}>
          Производитель прибора
          <input type="text" value={manufacturer} onChange={e => setManufacturer(e.target.value)}
            list="manufacturers-list" placeholder="Например: Schiller"
            style={{ display: "block", width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #c4ceda", marginTop: 4, boxSizing: "border-box" }} />
          <datalist id="manufacturers-list">
            {KNOWN_MANUFACTURERS.map(m => <option key={m} value={m} />)}
          </datalist>
        </label>
      </div>

      <label style={{ fontSize: "0.85rem", display: "block", marginBottom: 14 }}>
        Описание / клинический вопрос
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
          placeholder="Что смущает в записи, какой вопрос эксперту"
          style={{ display: "block", width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #c4ceda", marginTop: 4, boxSizing: "border-box" }} />
      </label>

      <label style={{ fontSize: "0.85rem", display: "block", marginBottom: 14 }}>
        Нативные файлы записи (до 10 файлов, до 500 МБ каждый)
        <div style={{ marginTop: 4 }}>
          <input type="file" multiple onChange={e => setSelectedFiles(Array.from(e.target.files))} />
        </div>
        {selectedFiles.length > 0 && (
          <div style={{ color: "#6b7280", fontSize: "0.8rem", marginTop: 4 }}>
            Выбрано: {selectedFiles.map(f => f.name).join(", ")}
          </div>
        )}
      </label>

      {error && (
        <div style={{ background: "#fee2e2", color: "#b91c1c", border: "1.5px solid #fecaca", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: "0.88rem" }}>
          {error}
        </div>
      )}

      <button type="submit" disabled={busy}
        style={{ background: "#e5005b", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
        {busy ? <Loader2 size={16} className="spin" /> : <UploadCloud size={16} />}
        {busy ? "Загрузка…" : "Отправить на второе мнение"}
      </button>
    </form>
  );
}

export default function SecondOpinion({ onLoginRequest }) {
  const { isLoggedIn, token, user } = useAuth();
  const [tab, setTab] = useState("submit");
  const [mine, setMine] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadMine = useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/second-opinion/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setMine)
      .finally(() => setLoading(false));
  }, [token]);

  const loadAssigned = useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/second-opinion/assigned`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setAssigned)
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (tab === "mine") loadMine();
    if (tab === "assigned") loadAssigned();
  }, [tab, loadMine, loadAssigned]);

  if (!isLoggedIn) {
    return (
      <section>
        <h1 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <HeartPulse size={20} strokeWidth={2.2} color="#e5005b" /> Второе мнение
        </h1>
        <p style={{ color: "#888" }}>
          Войдите, чтобы отправить запись на второе мнение.{" "}
          <button onClick={onLoginRequest} style={{ background: "none", border: "none", color: "#e5005b", fontWeight: 600, cursor: "pointer", padding: 0 }}>
            Войти
          </button>
        </p>
      </section>
    );
  }

  const tabStyle = active => ({
    padding: "8px 16px", borderRadius: 8, border: "1.5px solid " + (active ? "#e5005b" : "#e5e7eb"),
    background: active ? "#e5005b" : "#fff", color: active ? "#fff" : "#4b5563",
    fontWeight: 600, fontSize: "0.88rem", cursor: "pointer",
  });

  return (
    <section>
      <h1 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <HeartPulse size={20} strokeWidth={2.2} color="#e5005b" /> Второе мнение по ЭКГ/Холтеру/СМАД
      </h1>
      <p style={{ color: "#888", marginTop: -8, marginBottom: 16, fontSize: "0.92rem", maxWidth: 640 }}>
        Загрузите нативную запись с прибора (не скриншот) — эксперт даст письменное заключение
        или назначит очный онлайн-разбор случая.
      </p>

      {user?.verification_status !== "verified" && (
        <div style={{ background: "#fff1f6", color: "#9c0040", border: "1.5px solid #ffc2d9", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: "0.88rem", maxWidth: 640 }}>
          Отправлять записи может только верифицированный врач. Пройдите верификацию в профиле.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <button style={tabStyle(tab === "submit")} onClick={() => setTab("submit")}>Отправить заявку</button>
        <button style={tabStyle(tab === "mine")} onClick={() => setTab("mine")}>Мои заявки</button>
        <button style={tabStyle(tab === "assigned")} onClick={() => setTab("assigned")}>Назначено мне</button>
      </div>

      {tab === "submit" && user?.verification_status === "verified" && (
        <SubmitForm token={token} onCreated={() => { setTab("mine"); loadMine(); }} />
      )}

      {tab === "mine" && (
        loading ? <p style={{ color: "#888" }}>Загрузка…</p> :
        mine.length === 0 ? <p style={{ color: "#888" }}>Заявок пока нет.</p> :
        mine.map(r => <RequestCard key={r.id} req={r} token={token} />)
      )}

      {tab === "assigned" && (
        loading ? <p style={{ color: "#888" }}>Загрузка…</p> :
        assigned.length === 0 ? <p style={{ color: "#888" }}>Вам пока не назначали разборы.</p> :
        assigned.map(r => (
          <RequestCard key={r.id} req={r} token={token} canConclude
            onConcluded={updated => setAssigned(prev => prev.map(x => x.id === updated.id ? updated : x))} />
        ))
      )}
    </section>
  );
}
